import {
  ComboBox,
  IComboBox,
  IComboBoxOption,
  Label,
  SelectableOptionMenuItemType,
  Stack,
  Toggle,
} from "@fluentui/react";
import {
  FacetsEnum,
  IFacet,
  ISelection,
  Option,
} from "../types";
import { labelSort } from "../utilities/utilities";
import { useState } from "react";

interface StaticFacetProps {
  facet: IFacet;selection: ISelection,
  setSelection: (selection: ISelection) => void, isMulti?: boolean; selectedOptions: Option[]; filteredOptions: Option[]; setFacetValues: (selection: ISelection, setSelection: (selection: ISelection) => void) => (selectedFacets: {
      facet_code: string;
      values: string[];
  }[]) => void }

enum StaticOptions {
  SelectAll = "selectAll",
  Header = "header"
}

/**
 * Returns a sorted array of facet option fields. Includes the "header"
 * and "selectAll" items.
 */
function getOptions(filteredOptions: Option[], selectAllSelected: boolean, includeSelectAll: boolean) {
  const options: IComboBoxOption[] = filteredOptions
    .sort(labelSort)
    .map((o) => ({ key: o.value, text: o.label }));

  if (includeSelectAll) {
    options.unshift(
      {
        key: StaticOptions.SelectAll,
        text: "Select All",
        itemType: SelectableOptionMenuItemType.SelectAll,
        selected: selectAllSelected,
      },
      {
        key: StaticOptions.Header,
        text: "Options",
        itemType: SelectableOptionMenuItemType.Header,
      },
    );
  }

  return options;
}

/**
 * Returns the selection of facet options after a new selection has been made.
 * Takes into account the "select all" flag.
 */
function getNewSelection(
  facet: IFacet,
  selection: ISelection,
  option: IComboBoxOption,
  options: IComboBoxOption[],
  selectAll: boolean,
  isMulti?: boolean
) : string[] {
  const currentSelection =
    selection.filters.find((f) => f.facet === facet.code)?.values || [];
  if (
    option.itemType === SelectableOptionMenuItemType.SelectAll &&
    !selectAll
  ) {
    return options
      .filter((o) => o.key !== "selectAll" && o.key !== "header")
      .map((o) => String(o.key));
  }
  if (option.itemType === SelectableOptionMenuItemType.SelectAll && selectAll) {
    return [];
  }

  const optionSelected = option.selected === undefined
    ? true
    : option.selected;

  if (optionSelected && option.itemType !== SelectableOptionMenuItemType.SelectAll) {
    return isMulti
      ? [...currentSelection, option.key.toString()]
      : [option.key.toString()];
  }
  else {
    // remove the option from the current selection
    return currentSelection.filter((v) => v !== option.key.toString());
  }
}

/**
 * Displays a ComboBox, allowing the user to set a static facet filter.
 * Multiple values can be chosen, free form input is allowed (but will resolve to
 * one of the available options when the user hits "Enter")
 */
export default function StaticFacet({
  facet,
  selection,
  setSelection,
  setFacetValues,
  isMulti,
  selectedOptions,
  filteredOptions,
}: StaticFacetProps) {
  const [selectAll, setSelectAll] = useState(false);
  const options = getOptions(filteredOptions, selectAll, false);

  const handleChange = (
    e: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
  ) => {
    if (!option) return;

    if (option.itemType === SelectableOptionMenuItemType.SelectAll) {
      setSelectAll(!selectAll);
    }

    const newSelection = getNewSelection(
      facet,
      selection,
      option,
      options,
      selectAll,
      isMulti
    );

    if (options.length - 2 === newSelection.length) {
      setSelectAll(true);
    }

    if (newSelection.length === 0) {
      setSelectAll(false);
    }

    if (facet.code === FacetsEnum.Transformation) {
      setSelection({
        ...selection,
        transformations: newSelection,
      });
    } else if (facet.code === FacetsEnum.Indicator) {
      setSelection({
        ...selection,
        indicators: newSelection,
      });
    } else {
      setFacetValues(
        selection,
        setSelection,
      )([{ facet_code: facet.code, values: newSelection }]);
    }
  };

   const onChangeSummary = (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
  
          let filterForSummarizeFacet = selection.filters.filter(f => f.facet === facet.code);
  
          if (filterForSummarizeFacet.length === 0) {
              filterForSummarizeFacet = [{ facet: facet.code, summarize: !!checked, values: [] }];
          }
          else {
              filterForSummarizeFacet[0].summarize = !!checked;
          }
  
          const otherFilters = selection.filters.filter(f => f.facet !== facet.code);
  
          otherFilters.push(...filterForSummarizeFacet);
  
          const newSelection = {
              ...selection,
              filters: otherFilters
          };
  
          setSelection(newSelection);
      }

    const summarize = facet.showSummarize;
    const filterForFacet = selection.filters.filter(f => f.facet === facet.code)[0];
    
  return (
    <Stack
      horizontal={false}
      tokens={{
        childrenGap: 3,
        padding: 0,
      }}
      style={{ width: "100%" }}
    >
      <Label>{facet.name}</Label>
      
      {(!summarize || !filterForFacet?.summarize) && (
        <ComboBox
          multiSelect={isMulti}
          selectedKey={selectedOptions.map((o) => o.value)}
          allowFreeInput
          allowFreeform
          options={options}
          styles={{ root: { maxWidth: 500 } }}
          onChange={handleChange}
        />
      )}

      {summarize && (
          <Toggle
            onText={`Filter`}
            offText={`Aggregate`}
            onChange={onChangeSummary}
          />
        )}
    </Stack>
  );
}
